import {
  Alert,
  Collapse,
  Drawer,
  Group,
  Loader,
  SimpleGrid,
  Stack,
  Text,
  UnstyledButton,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconChevronDown, IconUser, IconUsersGroup } from '@tabler/icons-react';
import pluralize from 'pluralize';
import { ReactNode, useRef } from 'react';
import { FoundPersonSummary, UploadedFile } from 'src/data-capture/hooks/file-uploads/types';
import { Button } from 'src/design-system/Button';
import { iconFor } from './foundItemIcons';
import { FoundItem, FoundSection, sectionsOf, toFoundItem } from './foundItems';
import { formatBirthDate, initials } from './foundPersonDetails';
import classes from './FoundPersonDrawer.module.css';
import { useFoundPerson } from './useFoundPerson';

type Props = {
  file: UploadedFile;
  person: FoundPersonSummary;
  relativeFirstName: string | null;
  opened: boolean;
  onClose: () => void;
  onNotAClient: () => void;
  onAdd: () => void;
  addLabel: string;
};

const SHARED = 'shared';

const firstNameOf = (name: string) => name.split(' ')[0];

const describe = (person: FoundPersonSummary) => {
  const born = formatBirthDate(person.dateOfBirth);
  if (person.relationship) {
    return [`Described as ${person.relationship}`, born && `born ${born}`]
      .filter(Boolean)
      .join(' · ');
  }
  return born ? `Born ${born}` : null;
};

/** The headline number: the kind with the largest total, else how many kinds of thing were found. */
function highlight(sections: FoundSection[], items: FoundItem[]) {
  const biggest = sections
    .filter(section => section.totalAmount !== null)
    .sort((a, b) => (b.totalAmount ?? 0) - (a.totalAmount ?? 0))[0];
  if (biggest?.total) return { value: biggest.total, label: `in ${biggest.title.toLowerCase()}` };
  const kinds = new Set(items.map(item => item.label)).size;
  return { value: String(kinds), label: `${pluralize('kind', kinds)} of record` };
}

function ItemRow({ item, showKind }: { item: FoundItem; showKind: boolean }) {
  const [opened, { toggle }] = useDisclosure(false);
  const Icon = iconFor(item.entityType);
  return (
    <div className={classes.row} data-opened={opened || undefined} data-testid="found-item">
      <UnstyledButton className={classes.rowButton} onClick={toggle} aria-expanded={opened}>
        <span className={classes.rowText}>
          {showKind && item.name && (
            <span className={classes.type}>
              <Icon size={12} stroke={2.2} aria-hidden />
              {item.label}
            </span>
          )}
          <Text component="span" className={classes.itemName} lineClamp={2}>
            {item.name ?? item.label}
          </Text>
          {item.facts && (
            <Text component="span" className={classes.facts} truncate>
              {item.facts}
            </Text>
          )}
        </span>
        {(item.headline || item.sharing) && (
          <span className={classes.figure}>
            {item.headline}
            {item.sharing && <span className={classes.share}>{item.sharing}</span>}
          </span>
        )}
        <IconChevronDown size={16} className={classes.chevron} aria-hidden />
      </UnstyledButton>
      <Collapse expanded={opened}>
        <SimpleGrid cols={2} spacing="md" verticalSpacing="xs" className={classes.fields}>
          {item.fields.map(field => (
            <div key={field.key}>
              <Text className={classes.fieldLabel}>{field.label}</Text>
              <Text className={classes.fieldValue}>{field.value}</Text>
            </div>
          ))}
        </SimpleGrid>
      </Collapse>
    </div>
  );
}

function Section({
  anchor,
  head,
  items,
  showKind,
  tone,
}: {
  anchor: (element: HTMLElement | null) => void;
  head: ReactNode;
  items: FoundItem[];
  showKind: boolean;
  tone?: typeof SHARED;
}) {
  return (
    <section ref={anchor} className={classes.section}>
      <div className={classes.sectionHead}>{head}</div>
      <div className={classes.card} data-tone={tone}>
        {items.map(item => (
          <ItemRow key={item.id} item={item} showKind={showKind} />
        ))}
      </div>
    </section>
  );
}

export function FoundPersonDrawer({
  file,
  person,
  relativeFirstName,
  opened,
  onClose,
  onNotAClient,
  onAdd,
  addLabel,
}: Props) {
  const { data, isLoading, isError } = useFoundPerson(file, person.id, { enabled: opened });
  const anchors = useRef(new Map<string, HTMLElement>());
  const firstName = firstNameOf(person.name);
  const relative = relativeFirstName ?? 'the client';
  const own = (data?.ownItems ?? []).map(toFoundItem);
  const shared = (data?.sharedItems ?? []).map(toFoundItem);
  const sections = sectionsOf(own, `More about ${firstName}`);
  const description = describe(person);
  const ownCount = data ? own.length : person.ownCount;
  const sharedCount = data ? shared.length : person.sharedCount;
  const stats = [
    { value: String(ownCount), label: `${pluralize('item', ownCount)} about ${firstName} alone` },
    ...(data ? [highlight(sections, own)] : []),
    { value: String(sharedCount), label: `shared with ${relative}` },
  ];

  const anchor = (key: string) => (element: HTMLElement | null) => {
    if (element) anchors.current.set(key, element);
    else anchors.current.delete(key);
  };
  const jumpTo = (key: string) =>
    anchors.current.get(key)?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });

  return (
    <Drawer.Root
      opened={opened}
      onClose={onClose}
      position="right"
      size={580}
      padding={0}
      classNames={{ content: classes.content, body: classes.body }}
    >
      <Drawer.Overlay />
      <Drawer.Content data-testid="found-person-drawer">
        <header className={classes.header}>
          <Group justify="space-between" wrap="nowrap" gap="sm" align="flex-start">
            <Text className={classes.source}>Found in {file.file.fileName}</Text>
            <Drawer.CloseButton className={classes.close} aria-label="Close" />
          </Group>
          <Group wrap="nowrap" gap="md">
            <span className={classes.avatar} aria-hidden>
              {initials(person.name)}
            </span>
            <Stack gap={4} miw={0}>
              <Group gap="xs">
                <Drawer.Title className={classes.name}>{person.name}</Drawer.Title>
                <span className={classes.notClient}>Not a client yet</span>
              </Group>
              {description && <Text className={classes.described}>{description}</Text>}
            </Stack>
          </Group>
          <SimpleGrid cols={stats.length} spacing={8}>
            {stats.map(stat => (
              <Stack key={stat.label} gap={2} className={classes.stat} data-testid="found-stat">
                <Text className={classes.statValue}>{stat.value}</Text>
                <Text className={classes.statLabel}>{stat.label}</Text>
              </Stack>
            ))}
          </SimpleGrid>
        </header>
        <Drawer.Body>
          {isLoading && (
            <Group className={classes.status}>
              <Loader size="sm" />
            </Group>
          )}
          {isError && (
            <Alert color="error" m="lg">
              We couldn&apos;t load what we found. Please try again.
            </Alert>
          )}
          {data && (
            <>
              <nav className={classes.chips} aria-label="Jump to a kind of record">
                {sections.map(section => {
                  const Icon = section.entityType ? iconFor(section.entityType) : IconUser;
                  return (
                    <UnstyledButton
                      key={section.key}
                      className={classes.chip}
                      onClick={() => jumpTo(section.key)}
                    >
                      <Icon size={14} aria-hidden />
                      {section.title} {section.items.length}
                    </UnstyledButton>
                  );
                })}
                {shared.length > 0 && (
                  <UnstyledButton
                    className={classes.chip}
                    data-tone={SHARED}
                    onClick={() => jumpTo(SHARED)}
                  >
                    <IconUsersGroup size={14} aria-hidden />
                    Shared with {relative} {shared.length}
                  </UnstyledButton>
                )}
              </nav>
              <div className={classes.sections}>
                {sections.map(section => {
                  const Icon = section.entityType ? iconFor(section.entityType) : IconUser;
                  return (
                    <Section
                      key={section.key}
                      anchor={anchor(section.key)}
                      items={section.items}
                      showKind={section.entityType === null}
                      head={
                        <>
                          <span className={classes.sectionIcon}>
                            <Icon size={18} aria-hidden />
                          </span>
                          <Text component="h3" m={0} className={classes.sectionTitle}>
                            {section.title}{' '}
                            <span className={classes.count}>· {section.items.length}</span>
                          </Text>
                          {section.total && (
                            <Text className={classes.sectionTotal}>{section.total}</Text>
                          )}
                        </>
                      }
                    />
                  );
                })}
                {shared.length > 0 && (
                  <Section
                    anchor={anchor(SHARED)}
                    items={shared}
                    showKind
                    tone={SHARED}
                    head={
                      <>
                        <span className={classes.pair} aria-hidden>
                          <span className={classes.pairAvatar}>{initials(person.name)}</span>
                          <span className={classes.pairAvatar} data-tone="other">
                            {relativeFirstName ? initials(relativeFirstName) : ''}
                          </span>
                        </span>
                        <Stack gap={0} miw={0} style={{ flexGrow: 1 }}>
                          <Text component="h3" m={0} className={classes.sectionTitle}>
                            Shared with {relative}{' '}
                            <span className={classes.count}>· {shared.length}</span>
                          </Text>
                          <Text className={classes.sharedNote}>
                            Already on {relative}&apos;s review. Once added, {firstName} becomes a
                            co-owner.
                          </Text>
                        </Stack>
                      </>
                    }
                  />
                )}
              </div>
              <footer className={classes.footer}>
                <Text className={classes.footerNote} mb="sm">
                  Nothing here is on a client&apos;s record yet. &quot;Not a client&quot; deletes
                  it, and the shared items keep {firstName} as an outside owner.
                </Text>
                <Group justify="flex-end" gap="sm">
                  <Button variant="secondary" size="sm" onClick={onNotAClient}>
                    Not a client
                  </Button>
                  <Button variant="primary" size="sm" onClick={onAdd}>
                    {addLabel}
                  </Button>
                </Group>
              </footer>
            </>
          )}
        </Drawer.Body>
      </Drawer.Content>
    </Drawer.Root>
  );
}
